<script setup>
import DatePicker from 'primevue/datepicker';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Date],
        default: ''
    },
    defaultValue: {
        type: [String, Date],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    width: {
        type: String,
        default: '12.5rem'
    },
    errmsg: {
        type: String,
        default: ''
    },
    readonly: {
        type: Boolean,
        default: false
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    height: {
        type: String,
        default: '2.3rem'
    },
    minDate: {
        type: [String, Date],
        default: null
    },
    maxDate: {
        type: [String, Date],
        default: null
    }
});

const emit = defineEmits(['update:modelValue', 'date-select', 'input', 'change']);
const selectWidth = computed(() => props.width || '12.5rem');
const isReadonly = computed(() => props.readonly);

const parseLocalDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return new Date(value.getFullYear(), value.getMonth(), value.getDate());

    const text = String(value).trim();
    let match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match && /^\d{8}$/.test(text)) {
        match = [text, text.slice(0, 4), text.slice(4, 6), text.slice(6, 8)];
    }
    if (match) {
        const [, y, m, d] = match;
        return new Date(Number(y), Number(m) - 1, Number(d));
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const computedMinDate = computed(() => {
    if (!props.minDate) return null;
    return parseLocalDate(props.minDate);
});

const computedMaxDate = computed(() => {
    if (!props.maxDate) return null;
    return parseLocalDate(props.maxDate);
});

const formatDate = (date) => {
    if (!date) return '';

    if (typeof date === 'string') {
        if (/^\d{8}$/.test(date)) {
            return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
        }

        if (/^\d{4}-\d{2}-\d{2}/.test(date)) {
            return date.slice(0, 10);
        }
    }

    if (date instanceof Date && !isNaN(date)) {
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }

    return '';
};
const dateValue = ref(props.modelValue ? formatDate(props.modelValue) : null);
const datePickerRef = ref(null);
const inputError = ref(false); // 오류 상태를 저장할 변수 추가
const errMessage = ref(props.errmsg);

watch(
    () => props.modelValue,
    (newValue) => {
        dateValue.value = newValue ? formatDate(newValue) : '';
    },
    { immediate: true, deep: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const isDateInRange = (dateStringOrObject) => {
    if (!dateStringOrObject) return true;

    const normalizeDate = (d) => {
        if (!d) return null;
        return parseLocalDate(d);
    };

    const dateToCheck = normalizeDate(dateStringOrObject);
    if (!dateToCheck) return true;

    const min = normalizeDate(computedMinDate.value);
    const max = normalizeDate(computedMaxDate.value);

    if (min && dateToCheck < min) return false;
    if (max && dateToCheck > max) return false;

    return true;
};

watch(
    () => [props.modelValue, props.minDate, props.maxDate],
    ([newModelValue]) => {
        errMessage.value = '';

        if (newModelValue && !isDateInRange(newModelValue)) {
            nextTick(() => {
                errMessage.value = '선택 가능한 날짜 범위를 벗어났습니다.';
                dateValue.value = null;
                emit('update:modelValue', '');
                emit('input', '');
            });
        } else if (newModelValue) {
            dateValue.value = formatDate(newModelValue);
        } else {
            dateValue.value = null;
        }
    },
    { immediate: true, deep: true }
);

onMounted(() => {
    if (props.defaultValue) {
        dateValue.value = props.defaultValue;
        emit('update:modelValue', dateValue.value);
    }
});

const defaultPlaceholder = computed(() => {
    return props.placeholder || 'ex) ' + formatDate(new Date());
});

const parseDate = (value) => {
    // 하이픈과 문자 제거
    const numericValue = value.replace(/[^0-9]/g, '');

    // 8자리 숫자인 경우 (yyyyMMdd 형식)
    if (numericValue.length === 8) {
        const year = parseInt(numericValue.slice(0, 4), 10);
        const month = parseInt(numericValue.slice(4, 6), 10) - 1; // 0부터 시작
        const day = parseInt(numericValue.slice(6, 8), 10);
        const date = new Date(year, month, day);

        // 유효한 날짜인지 확인
        if (date.getFullYear() === year && date.getMonth() === month && date.getDate() === day) {
            return date;
        } else {
            errMessage.value = '유효하지 않은 날짜형식입니다.';
            return null;
        }
    }

    return value.replace(/[^0-9]/g, '').substring(0, 8);
};

const onDateSelect = (event) => {
    const date = formatDate(event);
    dateValue.value = date;
    emit('update:modelValue', date);
    emit('input', date);
    inputError.value = false; // 날짜 선택 시 오류 상태 초기화
    if (datePickerRef.value) {
        datePickerRef.value.overlayVisible = false;
    }
};

const onInput = (event) => {
    if (datePickerRef.value) {
        datePickerRef.value.overlayVisible = false; // DatePicker 닫기
    }
    errMessage.value = '';
    let value = event.target.value.replace(/[^0-9-]/g, '').substring(0, 10);
    const oldLength = value.length;
    const cursorPosition = event.target.selectionStart;

    // 날짜 형식에 맞게 하이픈 추가
    if (value.length > 4 && value[4] !== '-') {
        value = value.slice(0, 4) + '-' + value.slice(4);
    }
    if (value.length > 7 && value[7] !== '-') {
        value = value.slice(0, 7) + '-' + value.slice(7);
    }

    const hyphenPositions = [4, 7].filter((pos) => value[pos] === '-');
    let newCursorPosition = cursorPosition;

    if (hyphenPositions.length > 0 && cursorPosition === hyphenPositions[0] + 1) {
        newCursorPosition = cursorPosition + 1;
    }

    if (hyphenPositions.length > 1 && cursorPosition === hyphenPositions[1] + 1) {
        newCursorPosition = cursorPosition + 1;
    }

    dateValue.value = value;

    // 입력 필드 값 업데이트 및 커서 위치 조정
    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(newCursorPosition, newCursorPosition);
    });
    const date = parseDate(value);

    // console.log(value, ' ', date);

    if (date instanceof Date) {
        // 유효한 날짜인 경우
        //dateValue.value = date;
        const formattedDate = formatDate(date);
        dateValue.value = formattedDate;
        emit('update:modelValue', formattedDate);
        emit('input', formattedDate);
        //emit('change', formattedDate);
        inputError.value = false;
        if (datePickerRef.value) {
            datePickerRef.value.overlayVisible = false; // DatePicker 닫기
        }
    } else {
        // 유효하지 않은 날짜인 경우
        dateValue.value = value; // 원래 입력값 유지

        emit('update:modelValue', '');
        emit('input', '');

        //if (event.target.value.length > 0) ValidationMsg.value = '유효하지 않은 날짜입니다.';
        // emit('update:modelValue', value);
        // emit('input', value);
        //emit('date-select', ''); // 빈 문자열 대신 null을 사용할 수도 있습니다
        //inputError.value = value.length === 10; // 10자리 입력 시에만 오류 표시
    }

    // 입력 필드 값 업데이트
    //event.target.value = value;
};

const onFocus = (event) => {
    // if (event.target.value.length > 0) dateValue.value = event.target.value.replace(/[^0-9]/g, '').substring(0, 8);
    // else dateValue.value = event.target.value;
};

const onBlur = (event) => {
    if (dateValue.value) {
        dateValue.value = parseDate(event.value);
        if (dateValue.value === null) {
            errMessage.value = '';
            return;
        }

        if (!(dateValue.value instanceof Date) && dateValue.value !== '') errMessage.value = '유효하지 않은 날짜형식입니다.';
        else errMessage.value = '';
        //emit('date-select', formatDate(dateValue.value));
    }
};

const onChange = (event) => {
    // const value = event.target.value;
    // const date = parseDate(value);
    // dateValue.value = date; // Ensure dateValue is updated as Date object
    // const formattedDate = formatDate(date);
    // emit('update:modelValue', formattedDate);
    // emit('change', formattedDate);
};

const openDatePicker = () => {
    if (datePickerRef.value && !isReadonly.value) {
        datePickerRef.value.overlayVisible = true;
    }
};

const onTodayClick = () => {
    const today = new Date();
    const formattedDate = formatDate(today);
    dateValue.value = formattedDate;
    emit('update:modelValue', formattedDate);
    emit('input', formattedDate);
    if (datePickerRef.value) {
        datePickerRef.value.overlayVisible = false; // DatePicker 닫기
    }
};

const onClearClick = () => {
    errMessage.value = '';
    dateValue.value = null;
    emit('update:modelValue', '');
    emit('input', '');
    if (datePickerRef.value) {
        datePickerRef.value.overlayVisible = false; // DatePicker 닫기
    }
};

// watch(
//     () => dateValue.value,
//     (newValue) => {
//         if (datePickerRef.value) {
//             nextTick(() => {
//                 datePickerRef.value.currentValue = dateValue.value;
//                 //datePickerRef.value.updateCurrentDate();
//             });
//         }
//     }
// );
</script>

<template>
    <div
        v-if="visible"
        class="flex flex-col w-full"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="errMessage" class="validation-message ml-1">{{ errMessage }}</span>
        </label>
        <div class="relative w-full flex items-center h-12">
            <DatePicker
                ref="datePickerRef"
                v-model="dateValue"
                :inputId="inputId"
                :placeholder="defaultPlaceholder"
                :readonly="isReadonly"
                @input="onInput"
                @date-select="onDateSelect"
                @blur="onBlur"
                class="h-[2.5rem]"
                :class="['custom-datepicker', { 'error-border': errMessage, 'readonly-bg': isReadonly }]"
                dateFormat="yy-mm-dd"
                :manualInput="true"
                :showOnFocus="false"
                :style="{ textAlign: 'center', width: '100%' }"
                :minDate="computedMinDate"
                :maxDate="computedMaxDate"
            >
                <template #footer>
                    <div class="p-datepicker-buttonbar">
                        <button type="button" class="p-button-text custom-today-button" @click="onTodayClick">오늘</button>
                        <button type="button" class="p-button-text custom-clear-button" @click="onClearClick">초기화</button>
                    </div>
                </template>
            </DatePicker>
            <button v-if="dateValue && !isReadonly" @click="onClearClick" class="clear-button">&times;</button>
            <span v-if="!isReadonly" class="date-icon pi pi-calendar" @click="openDatePicker"></span>
        </div>
    </div>
</template>

<style scoped>
.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.label {
    font-family: 'SCoreDream600';
    color: #35424a;
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    user-select: none;
}

.custom-datepicker :deep(.p-inputtext) {
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

.custom-datepicker.readonly-bg :deep(.p-inputtext) {
    background-color: whitesmoke !important;
}
.readonly-Bg {
    background-color: whitesmoke !important;
}

.custom-datepicker.error-border :deep(.p-inputtext) {
    border-color: #f87171 !important; /* 빨간색 테두리 */
}

.custom-datepicker :deep(.p-inputtext:focus) {
    border-color: #0038ea !important;
    outline: none;
}

.custom-datepicker :deep(.p-inputtext[readonly]:focus) {
    border-color: #d1d5db !important;
    outline: none;
}

.clear-button {
    position: absolute;
    right: 2.5rem;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    width: 1rem;
    height: 1rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    background: transparent;
    border: none;
    opacity: 0.5;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

.clear-button:hover {
    font-family: 'SCoreDream500';
    opacity: 1;
}

.date-icon {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1rem;
    color: #6b7280;
    cursor: pointer;
}

.custom-today-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    margin-left: 0.5rem;
    color: black !important;
}

.custom-clear-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    margin-right: 0.5rem;
    color: black !important;
}

.validation-message {
    color: #f87171; /* red-500 */
    font-size: 0.75rem;
    margin-left: 0.5rem;
}
</style>
